/**
 * Social Auth Hook
 * Handles Google and Apple Sign In
 */

import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../config/env';
import { storage } from '../utils/storage';
import { UserData } from '../types/user';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          revoke: (email: string, callback: () => void) => void;
        };
        oauth2: {
          initCodeClient: (config: any) => { requestCode: () => void };
          initTokenClient: (config: any) => { requestAccessToken: () => void };
        };
      };
    };
    AppleID?: {
      auth: {
        init: (config: any) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}

interface AuthResponse {
  data: {
    jwt: string;
    user: {
      id: number;
      documentId: string;
      username: string;
      email: string;
      provider: string;
      confirmed: boolean;
      blocked: boolean;
    };
    isNewUser: boolean;
  };
  code: number;
  message: string;
}

interface OAuthConfig {
  google: {
    enabled: boolean;
    clientId: string | null;
  };
  apple: {
    enabled: boolean;
    clientId: string | null;
  };
}

export function useSocialAuth() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Fetch OAuth config on mount
  useEffect(() => {
    fetchOAuthConfig();
  }, []);

  const fetchOAuthConfig = async () => {
    try {
      const response = await axios.get<{ data: OAuthConfig }>(`${API_CONFIG}/auth/oauth-config`);
      setOauthConfig(response.data.data);
    } catch (err) {
      console.error('Failed to fetch OAuth config:', err);
      // Default to disabled if fetch fails
      setOauthConfig({
        google: { enabled: false, clientId: null },
        apple: { enabled: false, clientId: null },
      });
    } finally {
      setConfigLoading(false);
    }
  };

  /**
   * Handle successful authentication
   */
  const handleAuthSuccess = useCallback((response: AuthResponse) => {
    const { jwt, user } = response.data;
    const refreshToken = (response.data as any).refresh_token;

    // Store auth data
    storage.setJwtToken(jwt);
    if (refreshToken) storage.setRefreshToken(refreshToken);
    storage.setUserEmail(user.email);
    // Cast to UserData with default values for missing fields
    storage.setUserData({
      ...user,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: new Date().toISOString(),
      enableTotp: false,
    } as UserData);

    return { jwt, user, isNewUser: response.data.isNewUser };
  }, []);

  /**
   * Google Sign In using OAuth 2.0 Popup
   * Opens a centered popup window for Google account selection
   */
  const signInWithGoogle = useCallback(async (): Promise<{ success: boolean; user?: any }> => {
    setLoading(true);
    setError(null);

    try {
      if (!oauthConfig?.google.enabled || !oauthConfig?.google.clientId) {
        throw new Error('Google Sign-In is not configured');
      }

      // Load Google Identity Services script if not loaded
      if (!window.google) {
        await loadScript('https://accounts.google.com/gsi/client');
      }

      return new Promise((resolve, reject) => {
        // Use Google Identity Services with popup UX mode
        window.google!.accounts.id.initialize({
          client_id: oauthConfig.google.clientId,
          callback: async (response: any) => {
            try {
              // Send ID token to backend
              const authResponse = await axios.post<AuthResponse>(`${API_CONFIG}/auth/google`, {
                idToken: response.credential,
              });

              const result = handleAuthSuccess(authResponse.data);
              setLoading(false);
              resolve({ success: true, user: result.user });
            } catch (err: any) {
              const errorMsg = err.response?.data?.error?.message || err.message || 'Google authentication failed';
              setError(errorMsg);
              setLoading(false);
              reject(new Error(errorMsg));
            }
          },
          auto_select: false,
          ux_mode: 'popup', // Force popup mode
        });

        // Create a hidden container for Google button and click it
        const buttonContainer = document.createElement('div');
        buttonContainer.style.position = 'fixed';
        buttonContainer.style.top = '-9999px';
        buttonContainer.style.left = '-9999px';
        buttonContainer.id = 'google-signin-container';
        document.body.appendChild(buttonContainer);

        // Render the Google button (hidden) and programmatically click it
        window.google!.accounts.id.renderButton(buttonContainer, {
          type: 'standard',
          size: 'large',
          theme: 'outline',
          text: 'signin_with',
        });

        // Small delay to ensure button is rendered, then click it
        setTimeout(() => {
          const googleButton = buttonContainer.querySelector('div[role="button"]') as HTMLElement;
          if (googleButton) {
            googleButton.click();
          } else {
            // Fallback: try clicking the iframe's button
            const iframe = buttonContainer.querySelector('iframe');
            if (iframe) {
              // Can't click inside iframe, use prompt as fallback
              window.google!.accounts.id.prompt((notification: any) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                  setLoading(false);
                  setError('Unable to show Google Sign-In. Please try again.');
                  resolve({ success: false });
                }
              });
            }
          }
          // Clean up container after a delay
          setTimeout(() => {
            buttonContainer.remove();
          }, 60000); // Remove after 1 minute
        }, 100);
      });
    } catch (err: any) {
      const errorMsg = err.message || 'Google authentication failed';
      setError(errorMsg);
      setLoading(false);
      return { success: false };
    }
  }, [oauthConfig, handleAuthSuccess]);

  /**
   * Apple Sign In using Sign in with Apple JS
   */
  const signInWithApple = useCallback(async (): Promise<{ success: boolean; user?: any }> => {
    setLoading(true);
    setError(null);

    try {
      if (!oauthConfig?.apple.enabled || !oauthConfig?.apple.clientId) {
        throw new Error('Apple Sign-In is not configured');
      }

      // Load Apple Sign In script if not loaded
      if (!window.AppleID) {
        await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
      }

      // Initialize Apple Sign In
      window.AppleID!.auth.init({
        clientId: oauthConfig.apple.clientId,
        scope: 'name email',
        redirectURI: window.location.origin + '/auth/apple/callback',
        state: generateState(),
        usePopup: true,
      });

      // Trigger sign in
      const appleResponse = await window.AppleID!.auth.signIn();

      // Send identity token to backend
      const authResponse = await axios.post<AuthResponse>(`${API_CONFIG}/auth/apple`, {
        identityToken: appleResponse.authorization.id_token,
        authorizationCode: appleResponse.authorization.code,
        user: appleResponse.user, // Only available on first sign-in
      });

      const result = handleAuthSuccess(authResponse.data);
      setLoading(false);
      return { success: true, user: result.user };
    } catch (err: any) {
      const errorMsg = err.response?.data?.error?.message || err.message || 'Apple authentication failed';
      setError(errorMsg);
      setLoading(false);
      return { success: false };
    }
  }, [oauthConfig, handleAuthSuccess]);

  return {
    signInWithGoogle,
    signInWithApple,
    loading,
    error,
    oauthConfig,
    configLoading,
    isGoogleEnabled: oauthConfig?.google.enabled ?? false,
    isAppleEnabled: oauthConfig?.apple.enabled ?? false,
  };
}

/**
 * Load external script dynamically
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Generate random state for CSRF protection
 */
function generateState(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

export default useSocialAuth;
